/**
 * Mirrors: api/internal/httpapi/tenants.go and overview_tenants.go
 *
 *   GET    /tenants            the tenants this deployment holds
 *   POST   /tenants            create one (admin)
 *   PATCH  /tenants/:id        rename / enable / disable (admin)
 *   GET    /tenants/overview   one scored row per tenant
 *   GET    /modules/platform   the platform module switches
 *
 * These are PLATFORM paths. They must never be wrapped in tenantPath: they
 * belong to the deployment, not to a tenant, and every one of them answers 409
 * `module_disabled` while the multitenant module is off — except
 * /modules/platform, which answers either way so the switch itself can be
 * rendered.
 *
 * Two "unknown" states run through this file and neither may be flattened.
 * `credential_state: "unknown"` is not "no credential" and `scan_state:
 * "unknown"` is not "never scanned": they mean the server could not ask. Every
 * numeric field on an overview row is `number | null` for the same reason —
 * null is NOT MEASURED and is never rendered as 0.
 */
import { coerceArray, get, patch, post } from "@/lib/api";
import type { AppModule } from "./modules";
import type { Composition } from "./types";
import { normaliseSwitches, type FrameworkSwitches } from "./switches";

/** httpapi.TenantDTO. */
export interface Tenant {
  id: string;
  slug: string;
  display_name: string;
  enabled: boolean;
  is_default: boolean;
  /** The Entra directory this tenant's credential authenticates against. */
  entra_tenant_id: string | null;
  created_at: string;
  /**
   * FOUR measured states and one honest gap:
   *   no_credential  — nothing registered
   *   not_verified   — registered, never verified
   *   verified / failed — the last verification's outcome
   *   unknown        — the credential store could not be read. NOT "none".
   */
  credential_state: "unknown" | "no_credential" | "not_verified" | "verified" | "failed";
  credential_verified: boolean | null;
  credential_verified_at: string | null;
  /** `unknown` means the run store could not be read, not "never scanned". */
  scan_state: "unknown" | "never_scanned" | "scanned";
  last_run: TenantLastRun | null;
  /** The server's own words for a tenant with no finished run. */
  never_scanned?: string;
}

export interface TenantLastRun {
  id: string;
  status: string;
  trigger: string;
  finished_at: string | null;
}

/** httpapi's fleet score entry — the same pair discipline as a history point. */
export interface TenantScore {
  framework: string;
  profile_level?: string;
  /** D32 headline, pass vs ALL applicable checks; null = not measured. */
  score_all: number | null;
  /** Pass vs EXECUTED checks, the secondary score. */
  score: number | null;
  weighting?: string;
  composition?: Composition;
  coverage: number | null;
  evaluated: number;
  applicable: number;
}

export interface TenantRunCoverage {
  coverage: number | null;
  evaluated: number;
  applicable: number;
  no_result: number;
}

export interface TenantOverviewLastRun {
  run_id: string;
  status: string;
  trigger: string;
  started_at: string | null;
  finished_at: string | null;
  at: string;
}

/** httpapi.TenantOverviewRowDTO. */
export interface TenantOverviewRow {
  tenant_id: string;
  slug: string;
  display_name: string;
  enabled: boolean;
  is_default: boolean;
  /**
   * The all-checks score (framework "own"). Computed for machine consumers; the console no
   * longer shows it (D27).
   */
  score: TenantScore;
  /**
   * Critical & high (framework "own_base", field base_score): the critical and high checks,
   * with coverage over that universe. Normalised, never coerced to 0.
   */
  base_score: TenantScore;
  /**
   * Medium & low (framework "own_lower", field lower_score, D27): the medium, low and
   * informational checks, disjoint from base_score. Normalised, never coerced to 0.
   */
  lower_score: TenantScore;
  run_coverage: TenantRunCoverage;
  last_run: TenantOverviewLastRun | null;
  last_scan_at: string | null;
  never_scanned?: string;
  /** null when the counts could not be read. Never coerced to {}. */
  state_counts: Record<string, number> | null;
  failing: number | null;
  not_collected: number | null;
  /** null when the run store could not be asked. Not false. */
  scan_in_progress: boolean | null;
  /**
   * This tenant's switch state for ms_baseline, secure_score and ai_bp (D25), read
   * from its own settings. The rule set is always on and has no entry.
   */
  framework_switches: FrameworkSwitches;
}

export interface TenantsOverview {
  items: TenantOverviewRow[];
  note: string;
  /**
   * True when the multitenant module is off: the deployment serves exactly
   * one tenant, and `items` holds that tenant only.
   */
  single_tenant: boolean;
}

/**
 * Platform query keys.
 *
 * These carry NO tenant element on purpose — a platform list is the same list
 * whichever tenant the operator is looking at, and keying it per tenant would
 * refetch it on every switch.
 */
export const tenantKeys = {
  all: ["tenants"] as const,
  list: () => ["tenants", "list"] as const,
  overview: () => ["tenants", "overview"] as const,
  platformModules: () => ["modules", "platform"] as const,
};

function normaliseTenant(t: Tenant): Tenant {
  return {
    ...t,
    entra_tenant_id: t.entra_tenant_id ?? null,
    credential_state: t.credential_state ?? "unknown",
    credential_verified: t.credential_verified ?? null,
    credential_verified_at: t.credential_verified_at ?? null,
    scan_state: t.scan_state ?? "unknown",
    last_run:
      t.last_run === null || t.last_run === undefined
        ? null
        : { ...t.last_run, finished_at: t.last_run.finished_at ?? null },
  };
}

export async function fetchTenants(): Promise<Tenant[]> {
  const raw = await get<{ items: Tenant[] | null }>("/tenants");
  return coerceArray(raw.items).map(normaliseTenant);
}

export async function createTenant(input: {
  slug: string;
  display_name: string;
}): Promise<Tenant> {
  return normaliseTenant(await post<Tenant>("/tenants", input));
}

/** PATCH /tenants/:id — the UUID, not the slug. The slug is not editable. */
export async function updateTenant(
  id: string,
  input: { display_name?: string; enabled?: boolean },
): Promise<Tenant> {
  return normaliseTenant(await patch<Tenant>(`/tenants/${encodeURIComponent(id)}`, input));
}

function normaliseRow(r: TenantOverviewRow): TenantOverviewRow {
  return {
    ...r,
    score: {
      framework: r.score?.framework ?? "own",
      ...(r.score?.profile_level === undefined ? {} : { profile_level: r.score.profile_level }),
      score_all: r.score?.score_all ?? null,
      score: r.score?.score ?? null,
      ...(r.score?.composition === undefined ? {} : { composition: r.score.composition }),
      ...(r.score?.weighting === undefined ? {} : { weighting: r.score.weighting }),
      coverage: r.score?.coverage ?? null,
      evaluated: r.score?.evaluated ?? 0,
      applicable: r.score?.applicable ?? 0,
    },
    base_score: {
      framework: r.base_score?.framework ?? "own_base",
      score_all: r.base_score?.score_all ?? null,
      score: r.base_score?.score ?? null,
      ...(r.base_score?.composition === undefined ? {} : { composition: r.base_score.composition }),
      ...(r.base_score?.weighting === undefined ? {} : { weighting: r.base_score.weighting }),
      coverage: r.base_score?.coverage ?? null,
      evaluated: r.base_score?.evaluated ?? 0,
      applicable: r.base_score?.applicable ?? 0,
    },
    lower_score: {
      framework: r.lower_score?.framework ?? "own_lower",
      score_all: r.lower_score?.score_all ?? null,
      score: r.lower_score?.score ?? null,
      ...(r.lower_score?.composition === undefined ? {} : { composition: r.lower_score.composition }),
      ...(r.lower_score?.weighting === undefined ? {} : { weighting: r.lower_score.weighting }),
      coverage: r.lower_score?.coverage ?? null,
      evaluated: r.lower_score?.evaluated ?? 0,
      applicable: r.lower_score?.applicable ?? 0,
    },
    run_coverage: {
      coverage: r.run_coverage?.coverage ?? null,
      evaluated: r.run_coverage?.evaluated ?? 0,
      applicable: r.run_coverage?.applicable ?? 0,
      no_result: r.run_coverage?.no_result ?? 0,
    },
    last_run: r.last_run ?? null,
    last_scan_at: r.last_scan_at ?? null,
    // `?? {}` is EXACTLY the bug this product exists to prevent: a null map is
    // "could not be read", an empty map is "read, and nothing was counted".
    state_counts: r.state_counts ?? null,
    failing: r.failing ?? null,
    not_collected: r.not_collected ?? null,
    framework_switches: normaliseSwitches(r.framework_switches),
    scan_in_progress: r.scan_in_progress ?? null,
  };
}

export async function fetchTenantsOverview(): Promise<TenantsOverview> {
  const raw = await get<TenantsOverview>("/tenants/overview");
  return {
    items: coerceArray(raw.items).map(normaliseRow),
    note: raw.note ?? "",
    single_tenant: raw.single_tenant === true,
  };
}

/**
 * The platform module list. One row today: `multitenant`.
 *
 * It answers with the module OFF as well as on — the switch that turns it on
 * lives on the platform settings page, and a list that 409'd while off would
 * make that page unable to render its own control.
 */
export async function fetchPlatformModules(): Promise<AppModule[]> {
  const raw = await get<{ items: AppModule[] | null }>("/modules/platform");
  return coerceArray(raw.items);
}
